import {
    Component,
    OnInit,
    OnChanges,
    Input,
    Output,
    EventEmitter,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { RtTripUK, RtClmView, RtvTripError, RtvTripErrorAdapter } from '@bds/railtrac-models';
import { PopoutEvent } from '../../popout-event';
import {
    faSpinner,
    faCheck,
    faExclamation,
    faClipboard,
    faPlus,
    faArrowRotateRight,
    faFileImport,
} from '@fortawesome/pro-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/pro-duotone-svg-icons';
import { faTrain, faClmHistory, faSave } from '@bds/fa-svg-icons';
import { RtDiversionService } from '../../rt-diversion';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ScrollSpyService } from '@bds/helpers';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { EquipReportCategoryService, EquipReportCatgValueService } from '@bds/equipment';
import { EquipReportCategory, EquipReportCatgValue } from '@bds/equipment';
import {
    BdsCustomerService,
    CommodityShippedService,
    RtCustomerTypeDefaultService,
    ClmSightCodeService,
} from '@bds/data-access';
import { ErrorListItem } from '@bds/rt-advance-models';
import { RtCommodityShippedForm } from '../../railtrac-trip/rt-commodity-shipped-form.service';
import { RtTripErrorService } from '@bds/railtrac';
import { CommunicationMessage, CommunicationService } from '@bds/core';
import { Router } from '@angular/router';

interface ISelectedTrip {
    id: number;
    tripError: RtvTripError;
    key: RtTripUK;
    state: 'none' | 'saving' | 'saved' | 'failed';
    errors?: ErrorListItem[];
}

@Component({
    selector: 'rt-trip-error-multi',
    templateUrl: './rt-trip-error-multi.component.html',
    styleUrls: ['./rt-trip-error-multi.component.scss'],
    providers: [RtCommodityShippedForm],
})
export class RtTripErrorMultiComponent implements OnInit, OnChanges {
    @Input() ormIds: number[];
    @Input() allowPopout: boolean;
    @Input() isReadOnly = false;
    @Output() popout: EventEmitter<PopoutEvent<number>> = new EventEmitter<PopoutEvent<number>>();

    currentHttpRequest: Subscription;
    selectedTripKeys: RtTripUK[];
    selectedTrips: ISelectedTrip[];
    selectedTripError: RtvTripError;
    tripErrors: RtvTripError[];

    currentClm: RtClmView;
    sightCodes: Observable<any[]>;
    disabledClmFields: string[] = [];
    routeReadOnly = true;

    equipCategoriesGroup: { [key: number]: EquipReportCategory };
    equipCatgValuesGroup: { [key: number]: EquipReportCatgValue };

    isDirty = false;
    isSaving = false;
    loading = false;

    iconSpinner = faSpinner;
    iconTrain = faTrain;
    iconAdd = faPlus;
    reprocess = faArrowRotateRight;
    iconDelete = faTrashAlt;
    iconStatusSaved = faCheck;
    iconStatusSaving = faSpinner;
    iconStatusFailed = faExclamation;
    iconStatusNone = faClipboard;
    history = faClmHistory;
    save = faSave;
    import = faFileImport;

    @ViewChild('tripdiversionpanel') tripdiversionpanel: MatExpansionPanel;
    @ViewChild('tripErrorDetails') tripErrorDetailsComponent: any;

    constructor(
        public tripErrorService: RtTripErrorService,
        private equipReportCategoryService: EquipReportCategoryService,
        private equipReportCatgValueService: EquipReportCatgValueService,
        private _snackbar: MatSnackBar,
        public scrollSpyService: ScrollSpyService,
        public dialog: MatDialog,
        public tripDiversionService: RtDiversionService,
        private commodityService: CommodityShippedService,
        private customerApiService: BdsCustomerService,
        public communicationService: CommunicationService,
        public customerTypeDefaultService: RtCustomerTypeDefaultService,
        private clmSightCodeService: ClmSightCodeService,
        private router: Router,
        private adapter: RtvTripErrorAdapter,
    ) {}

    ngOnInit() {
        this.getCategories();
        this.getCategoryValues();
        this.sightCodes = this.clmSightCodeService.getAll();
    }

    //#region EquipmentCategory
    getCategories(): void {
        this.equipReportCategoryService
            .getAll()
            .pipe(take(1))
            .subscribe((res) => {
                const grp: { [key: number]: EquipReportCategory } = {};
                res.map((m) => {
                    grp[m.ormId] = m;
                });
                this.equipCategoriesGroup = grp;
            });
    }

    reProcess(): void {
        this.isSaving = true;

        // Set all selected trips to 'saving' state when method starts
        if (this.selectedTrips) {
            this.selectedTrips.forEach((trip) => {
                trip.state = 'saving';
            });
        }

        this.tripErrorService.reProcess(this.ormIds).subscribe({
            next: (result) => {
                this.isSaving = false;
                if (result && result.relatedObject.length === 0) {
                    // All trips processed successfully - set all to 'saved'
                    if (this.selectedTrips) {
                        this.selectedTrips.forEach((trip) => {
                            trip.state = 'saved';
                        });
                    }

                    this._snackbar.open('Trip(s) reprocessed successfully.', 'Ok', {
                        duration: 3000,
                    });
                    const message: CommunicationMessage = {
                        subscriberName: 'TripErrorScreen',
                        action: 'reprocess',
                        data: result.relatedObject,
                    };
                    this.communicationService.sendData(message);

                    void this.router.navigate(['interface-validation'], {
                        queryParamsHandling: 'preserve',
                    });
                } else {
                    // Some trips failed - set failed trips to 'failed' state and successful ones to 'saved'
                    if (this.selectedTrips && result.relatedObject) {
                        const failedIds = result.relatedObject.map((obj) => obj.ormId);
                        this.selectedTrips.forEach((trip) => {
                            if (failedIds.includes(trip.id)) {
                                trip.state = 'failed';
                            } else {
                                trip.state = 'saved';
                            }
                        });
                    }

                    this._snackbar.open(
                        `Reprocess failed for trip(s) ${result.relatedObject
                            .map((o) => o.carInit + o.carNo)
                            .join(', ')}.`,
                        'Info',
                        {
                            duration: 3000,
                        },
                    );
                    const message: CommunicationMessage = {
                        subscriberName: 'TripErrorScreen',
                        action: 'reprocess',
                        data: result.relatedObject,
                    };
                    this.communicationService.sendData(message);
                }
            },
            error: (err) => {
                this.isSaving = false;

                // Set all trips to 'failed' state on error
                if (this.selectedTrips) {
                    this.selectedTrips.forEach((trip) => {
                        trip.state = 'failed';
                    });
                }

                this._snackbar.open('Error reprocessing trip(s).', 'Error', {
                    duration: 3000,
                });
            },
            complete: () => {
                this.isSaving = false;
            },
        });
    }

    onDeleteProductClick(ormId: number): void {
        this.commodityService
            .delete(ormId)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this._snackbar.open('Product deleted.', 'Ok');
                    // this.selectedCommodityShippedKeys = this.selectedCommodityShippedKeys.filter(
                    //     (data) => data.ormId !== ormId,
                    // );
                },
                error: () => {
                    this._snackbar.open('Error during delete.', 'Ok');
                },
            });
    }

    getCategoryValues(): void {
        this.equipReportCatgValueService
            .getAll()
            .pipe(take(1))
            .subscribe((res) => {
                const grp: { [key: number]: EquipReportCatgValue } = {};
                res.map((m) => {
                    grp[m.ormId] = m;
                });
                this.equipCatgValuesGroup = grp;
            });
    }

    async fetchData(): Promise<void> {
        if (!this.ormIds || this.ormIds.length === 0) {
            return;
        }
        // this.loading = true;

        const a = await firstValueFrom(this.tripErrorService.readCombined(this.ormIds));
        this.selectedTripError = a.trip;
    }

    private carIdSort = (
        a: { carInit: string; carNo: string },
        b: { carInit: string; carNo: string },
    ) => {
        const nameA = `${(a.carInit + a.carNo).toUpperCase()}`;
        const nameB = `${(b.carInit + b.carNo).toUpperCase()}`;
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        // names must be equal
        return 0;
    };

    ngOnChanges(changes: SimpleChanges) {
        const tripIdChange = changes['ormIds'];

        if (!!tripIdChange) {
            const tripIdCurrent: number[] = tripIdChange.currentValue;
            this.fetchData();
        }
    }

    getTripInitiateStatus(custNo: string, le: string): Observable<string> {
        return this.customerApiService.getByCustNo(custNo).pipe(
            switchMap((customer) => {
                return this.customerTypeDefaultService
                    .getByCustTypeAndLE(customer?.customerType, le)
                    .pipe(
                        map((customerType) => {
                            return customerType ? customerType.beginStatus : '';
                        }),
                    );
            }),
        );
    }

    /**
     * set selected section
     * @param section string
     */
    setCurrentSection(section: string): void {
        this.scrollSpyService.changeSection(section);
    }

    showTrip(ormId: number): void {
        // Update selectedTripError from tripErrors list based on ormId
        const tripError = this.tripErrors?.find((te) => te.ormId === ormId);
        if (tripError) {
            this.selectedTripError = tripError;

            // Force change detection to ensure the details component is properly updated
            setTimeout(() => {
                // This ensures the rt-trip-error-details component has time to reinitialize
                // with the new selectedTripError data
            }, 0);
        }
    }

    reloadClms(): void {
        // Reload CLM data for the current selected trip
        if (this.selectedTripError?.ormId) {
        }
    }

    async processTrips(useAddImport: boolean = false): Promise<void> {
        if (!this.ormIds || this.ormIds.length === 0) {
            this._snackbar.open('No trips selected for import', 'Warning', { duration: 3000 });
            return;
        }

        this.isSaving = true;

        try {
            // Get only the changed form values
            const changedFormValues = this.getChangedFormValues();

            if (
                (!changedFormValues || Object.keys(changedFormValues).length === 0) &&
                !useAddImport
            ) {
                this._snackbar.open('No form changes detected to update', 'Info', {
                    duration: 3000,
                });
                this.isSaving = false;
                return;
            }

            // Create array to store updated trip errors
            const updatedTripErrors: RtvTripError[] = [];

            // Process each ormId (exclude the currently selected one)
            const ormIdsToUpdate = this.ormIds.filter((id) => id !== this.selectedTripError?.ormId);

            for (const ormId of ormIdsToUpdate) {
                try {
                    // Get the individual trip error
                    const result = await firstValueFrom(this.tripErrorService.read(ormId));

                    const tripError = this.adapter.adapt(result);

                    if (tripError) {
                        const updatedTripError: RtvTripError = {
                            ...tripError, // Keep all original values
                            ...changedFormValues, // Only override with changed values
                        } as RtvTripError;

                        updatedTripErrors.push(updatedTripError);
                    }
                } catch (error) {
                    console.error(`Error processing trip error for ormId ${ormId}:`, error);
                }
            }

            if (updatedTripErrors.length > 0) {
                // Save all updated trip errors using the appropriate method
                await this.saveUpdatedTripErrors(updatedTripErrors, useAddImport);

                // Create car ID list for the message
                const carIds = updatedTripErrors
                    .map((trip) => `${trip.carInit}${trip.carNo}`)
                    .join(', ');

                this._snackbar.open(
                    `Successfully ${useAddImport ? 'imported' : 'updated'} data to ${
                        updatedTripErrors.length
                    } trip(s): ${carIds}`,
                    'Success',
                    { duration: 5000 },
                );

                const message: CommunicationMessage = {
                    subscriberName: 'TripErrorScreen',
                    action: 'refresh',
                    data: updatedTripErrors,
                };
                this.communicationService.sendData(message);
                // Refresh the data to show updates
                await this.fetchData();
            } else {
                this._snackbar.open('No trips were updated', 'Info', { duration: 3000 });
            }
        } catch (error) {
            console.error(`Error in ${useAddImport ? 'addImports' : 'update'}:`, error);
            this._snackbar.open(
                `Error ${useAddImport ? 'importing' : 'updating'} data to trips`,
                'Error',
                { duration: 3000 },
            );
        } finally {
            this.isSaving = false;
        }
    }

    // Simplified public methods
    async addImports(): Promise<void> {
        await this.processTrips(true);
    }

    async update(): Promise<void> {
        await this.processTrips(false);
    }

    private getChangedFormValues(): Partial<RtvTripError> {
        if (!this.tripErrorDetailsComponent?.tripForm) {
            console.warn('Trip form not available');
            return {};
        }

        const form = this.tripErrorDetailsComponent.tripForm;
        const changedValues: any = {};

        const importableFields = [
            'fleetId',
            'businessGroup',
            'custNo',
            'routeCode',
            'shipDateTime',
            'shipmentType',
            'origCity',
            'origState',
            'destCity',
            'destState',
            'returnCity',
            'returnState',
            'supplierFact',
            'freeRunner',
            'shopMove',
            'salesRep',
            'oneWayRound',
            'badOrderDays',
            'divertYorn',
            'freightPmtCode',
            'unitTrainId',
            'tripRef1',
            'tripRef2',
            'tripRef3',
            'tripRef4',
            'csrNo',
            'tripNo',
            'bolNo',
            'shipperNo',
            'custPoNo',
            'tripStatus',
            'carStatus',
            'freeRunner',
            'supplierShipment',
            'supplierCode',
        ];

        importableFields.forEach((fieldName) => {
            const control = form.get(fieldName);

            if (control && control.dirty) {
                // Only include if the value has actually changed and is not empty/null
                const value = control.value;

                if (value !== null && value !== undefined && value !== '') {
                    changedValues[fieldName] = value;
                }
            }
        });

        return changedValues as Partial<RtvTripError>;
    }

    private async saveUpdatedTripErrors(
        updatedTripErrors: RtvTripError[],
        useAddImport: boolean,
    ): Promise<void> {
        const savePromises = updatedTripErrors.map((tripError) =>
            firstValueFrom(
                useAddImport
                    ? this.tripErrorService.addImport(tripError)
                    : this.tripErrorService.updateInterfaceValidation(tripError),
            ),
        );

        try {
            await Promise.all(savePromises);
        } catch (error) {
            throw error;
        }
    }
}
